import React, { useState, useCallback, useRef, useEffect } from 'react'
import QuizContent from '../shared/QuizContent'
import QuizTitle from '../shared/QuizTitle'
import QuizOptions from '../shared/QuizOptions'
import QuizOption from '../shared/QuizOption'
import LazyImage from '../shared/LazyImage'
import { getQuizOptionsData } from '../../constants'
import { loadCharacterAttributes } from '../../utils/api'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { trackStep2 } from '../../utils/analytics'

const Step1 = ({ onAnswer, onPrevious, selections, isTransitioning }) => {
  const location = useLocation()
  const isHalloween = !!location.pathname.includes('/halloween')
  const selectedStyle = selections?.welcome || 'Realistic'
  const ethnicities = getQuizOptionsData('ethnicity', selectedStyle)

  const [selectedEthnicity, setSelectedEthnicity] = useState(() => {
    return ethnicities && ethnicities.length > 0 ? ethnicities[0].value : ''
  })
  const [selectedAge, setSelectedAge] = useState(22)
  const [attributesMap, setAttributesMap] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const sliderRef = useRef()

  useEffect(() => {
    ;(async () => {
      const map = await loadCharacterAttributes()
      setAttributesMap(map)
      setIsLoading(false)
    })()
  }, [])

  useEffect(() => {
    const value = selectedAge
    const min = 21
    const max = 55
    const percent = ((value - min) / (max - min)) * 100

    if (sliderRef.current) {
      sliderRef.current.style.background = isHalloween
        ? `linear-gradient(100.44deg, #ED712B 0%, #D03C3C ${percent}% , rgba(255,255,255,0.05) ${percent}%)`
        : `linear-gradient(90deg, #8350CB 0%, #FE6283 ${percent}%, rgba(255,255,255,0.05) ${percent}%)`
    }
  }, [selectedAge])

  const ethnicityLabelMap = { Arab: 'Mid.Eastern' }

  const mapAgeToApiLabel = age => {
    if (age >= 18 && age <= 19) return '18+'
    if (age >= 20 && age <= 29) return '20s'
    if (age >= 30 && age <= 39) return '30s'
    return '40+'
  }

  const getAttributeId = (type, label) => {
    if (!attributesMap[type] || !label) return null

    if (type === 'ethnicity' && ethnicityLabelMap[label]) {
      label = ethnicityLabelMap[label]
    }

    if (type === 'age') {
      label = mapAgeToApiLabel(label)
    }

    return attributesMap[type][label.trim()] || null
  }

  const handleEthnicityClick = useCallback(
    value => {
      if (isTransitioning || isLoading) return

      setSelectedEthnicity(value)
      const id = getAttributeId('ethnicity', value)
      console.log('📝 Ethnicity answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap, isTransitioning, isLoading],
  )

  const handleAgeChange = useCallback(
    event => {
      if (isTransitioning || isLoading) return

      const value = parseInt(event.target.value)
      setSelectedAge(value)

      const min = parseInt(event.target.min)
      const max = parseInt(event.target.max)
      const percent = ((value - min) / (max - min)) * 100
      event.target.style.background = isHalloween
        ? `linear-gradient(100.44deg, #ED712B 0%, #D03C3C ${percent}% , rgba(255,255,255,0.05) ${percent}%)`
        : `linear-gradient(90deg, #8350CB 0%, #FE6283 ${percent}%, rgba(255,255,255,0.05) ${percent}%)`

      const ageId = getAttributeId('age', value)
      console.log('📝 Age answer:', value)
      console.log('🔗 API ID:', ageId)
    },
    [attributesMap, isTransitioning, isLoading],
  )

  const handleContinue = useCallback(() => {
    if (isTransitioning || isLoading || !canContinue) return

    const ethnicityId = getAttributeId('ethnicity', selectedEthnicity)
    const ageId = getAttributeId('age', selectedAge)

    console.log('Continue clicked with:', {
      ethnicity: selectedEthnicity,
      ethnicityId,
      age: selectedAge,
      ageId,
    })

    trackStep2(selectedAge, selectedEthnicity)

    onAnswer('step1', {
      ethnicity: selectedEthnicity,
      age: selectedAge,
    })
  }, [selectedEthnicity, selectedAge, attributesMap, onAnswer, isTransitioning, isLoading])

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return
    onPrevious()
  }, [onPrevious, isTransitioning])

  const canContinue = selectedEthnicity && selectedAge
  const { t } = useTranslation()

  return (
    <div className={`quizContentWrapper ${isTransitioning ? 'no-click' : ''}`}>
      <QuizContent className='step1-design'>
        {/* Ethnicity Block */}
        <div className='step1-block'>
          <QuizTitle>
            <>
              {t('choose')}
              <span className='specialText'> {t('ethnicity_text')}</span>
            </>
          </QuizTitle>
          <QuizOptions className='ethnicity-options'>
            {ethnicities.map(ethnicity => {
              const isActive = selectedEthnicity === ethnicity.value

              return (
                <QuizOption
                  key={ethnicity.value}
                  value={ethnicity.value}
                  name='ethnicity'
                  onClick={() => handleEthnicityClick(ethnicity.value)}
                  className={`ethnicity-option ${isActive ? 'selected' : selectedEthnicity ? 'not-selected' : ''} ${
                    ethnicities.length % 2 === 0 ? 'even' : 'odd'
                  }`}
                >
                  <div className='ethnicity-image'>
                    <LazyImage src={ethnicity.leftGirl} className={`leftGirlStep1 ${isActive ? 'active' : ''}`} alt={ethnicity.alt} />
                    <LazyImage src={ethnicity.image} className={`centeredGirlStep1 ${isActive ? 'active' : ''}`} alt={ethnicity.alt} />
                    <LazyImage src={ethnicity.rightGirl} className={`rightGirlStep1 ${isActive ? 'active' : ''}`} alt={ethnicity.alt} />
                  </div>
                  <div className='image-title'>{t(ethnicity.text)}</div>
                </QuizOption>
              )
            })}
          </QuizOptions>
        </div>

        {/* Age Block */}
        <div className='step1-block'>
          <QuizTitle>
            <>
              {t('choose')}
              <span className='specialText'> {t('age_text')}</span>
            </>
          </QuizTitle>
          <div className='age-selector'>
            <div className='age-selector-block'>
            <div className={`age-display ${selectedAge ? 'selected' : ''}`}>
              {selectedAge} {t('years')}
            </div>
            <div className='age-slider-container'>
              <div className='age-slider-num'>21+</div>

              <input
                ref={sliderRef}
                type='range'
                min='21'
                max='55'
                value={selectedAge}
                onChange={handleAgeChange}
                className='age-slider'
                disabled={isTransitioning || isLoading}
              />

              <div className='age-slider-num'>55+</div>
            </div>
            </div>
          </div>
        </div>

        <div className='navigation-wrapper step1-navigation-wrapper'>
          <button className='previous-button' onClick={handlePrevious} disabled={isTransitioning}>
            {t('previous')}
          </button>
          <button className='continue-button' onClick={handleContinue} disabled={!canContinue || isTransitioning || isLoading}>
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  )
}

export default Step1
